import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ConfirmContext = createContext(null);

/**
 * Provider globale per dialog di conferma.
 * Espone `confirm(title, description?, opts?)` che ritorna Promise<boolean>.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Eliminare?', 'Operazione irreversibile.', { destructive: true });
 *   if (!ok) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Conferma',
    cancelLabel: 'Annulla',
    destructive: false,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((title, description = '', {
    confirmLabel = 'Conferma',
    cancelLabel = 'Annulla',
    destructive = false,
  } = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, title, description, confirmLabel, cancelLabel, destructive });
    });
  }, []);

  const handleConfirm = () => {
    setState(s => ({ ...s, open: false }));
    resolverRef.current?.(true);
  };

  const handleCancel = () => {
    setState(s => ({ ...s, open: false }));
    resolverRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={open => { if (!open) handleCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription>{state.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>{state.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={state.destructive ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600' : ''}
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
